import { FunctionComponent, useCallback, useEffect, useState } from 'react'

import styled, { css, useTheme } from 'styled-components'

import useCurrentSection from '@hooks/useCurrentSection'
import { MainMenuItem } from '@hooks/useNavigation'
import usePageMetadata from '@hooks/usePageMetadata'
import useToggleMenuVisibility from '@hooks/useToggleMenuVisibility'
import Blink from '@components/Blink'
import ArrowButton from '@components/Header/FirstRow/BurgerMenu/ArrowButton'

import useTrackingUtils from '../../useTrackingUtils'
import SecondLevelMenu from './SecondLevelMenu'

const ItemWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing20, spacing16 },
      colors: { strokeWeak },
    },
  }) => css`
    height: 48px;
    padding: ${spacing16} ${spacing20} ${spacing16} ${spacing20};
    display: flex;
    flex-direction: row;
    align-items: center;
    box-sizing: border-box;
    border-top: 1px solid ${strokeWeak};
  `}
`

interface StyledBlinkProps {
  isActive: boolean
  sectionColor: string
}

const StyledBlink = styled(Blink)<StyledBlinkProps>`
  ${({
    theme: {
      colors: { textDefault, textBrand },
      typography: {
        headings: {
          small: { bundledCSS: smallHeadingCSS },
        },
      },
    },
    isActive,
  }) => css`
    color: ${isActive ? textBrand : textDefault};
    text-decoration: none;
    flex: 1 auto;
    ${smallHeadingCSS}
  `}
`

const StyledItem = styled.button<StyledBlinkProps>`
  ${({
    theme: {
      colors: { textDefault, textBrand },
      typography: {
        headings: {
          small: { bundledCSS: smallHeadingCSS },
        },
      },
    },
    isActive,
  }) => css`
    color: ${isActive ? textBrand : textDefault};
    background-color: transparent;
    border: 0;
    cursor: pointer;
    padding: 0;
    text-align: left;
    flex-grow: 1;
    ${smallHeadingCSS}
  `}
`

const ItemAndSubmenuWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const FirstLevelItem: FunctionComponent<MainMenuItem> = ({
  title,
  primaryColor,
  link,
  content,
}) => {
  const { isActive } = useCurrentSection(link.title || '')
  const theme = useTheme()
  const [isSubmenuVisible, setIsSubMenuVisible] = useState(isActive)
  const [mountChildren, setMountChildren] = useState(false)
  const pageMetadata = usePageMetadata()

  const { burgerMenuClickTrackingFn } = useTrackingUtils({
    title,
    url: link.href,
  })

  const { closeBurgerMenu } = useToggleMenuVisibility()

  const handleClick = useCallback(() => {
    burgerMenuClickTrackingFn()
    closeBurgerMenu(true)
  }, [burgerMenuClickTrackingFn, closeBurgerMenu])

  useEffect(() => {
    if (isSubmenuVisible) {
      setMountChildren(true)
    }
  }, [isSubmenuVisible])

  useEffect(() => {
    setIsSubMenuVisible(isActive)
  }, [isActive, pageMetadata])

  const toggleSubmenu = () => setIsSubMenuVisible((prevValue) => !prevValue)

  return (
    <ItemAndSubmenuWrapper>
      <ItemWrapper>
        {!link.href ? (
          <StyledItem
            onClick={toggleSubmenu}
            aria-label={title}
            isActive={isSubmenuVisible}
            sectionColor={primaryColor}
          >
            {title}
          </StyledItem>
        ) : (
          <StyledBlink
            {...link}
            isActive={isSubmenuVisible}
            sectionColor={primaryColor}
            onClick={handleClick}
          >
            {title}
          </StyledBlink>
        )}
        {content?.length && (
          <ArrowButton
            isExpanded={isSubmenuVisible}
            iconColor={theme.colors.iconDefault}
            onClick={toggleSubmenu}
          />
        )}
      </ItemWrapper>
      {mountChildren && (
        <SecondLevelMenu isVisible={isSubmenuVisible}>
          {content}
        </SecondLevelMenu>
      )}
    </ItemAndSubmenuWrapper>
  )
}

export default FirstLevelItem
